import { AxiosClient } from '_api/AxiosClient';
import { config } from '_config';

const { accountingApiUrl } = config;

class AccountingApi extends AxiosClient {
  constructor(url) {
    super(url);
    this.getPlaidToken = this.getPlaidToken.bind(this);
    this.addPlaidAccount = this.addPlaidAccount.bind(this);
    this.getOAuthUrl = this.getOAuthUrl.bind(this);
    this.authenticate = this.authenticate.bind(this);
    this.getAccounts = this.getAccounts.bind(this);
    this.addAccount = this.addAccount.bind(this);
    this.deleteAccount = this.deleteAccount.bind(this);
    this.getMessages = this.getMessages.bind(this);
    this.postMessage = this.postMessage.bind(this);
    this.markMessages = this.markMessages.bind(this);
    this.getGraph = this.getGraph.bind(this);
    this.getBankTransactions = this.getBankTransactions.bind(this);
    this.addBankTransaction = this.addBankTransaction.bind(this);
    this.updateBankTransaction = this.updateBankTransaction.bind(this);
    this.deleteBankTransaction = this.deleteBankTransaction.bind(this);
    this.getTransactionCategories = this.getTransactionCategories.bind(this);
    this.addTransactionCategory = this.addTransactionCategory.bind(this);
    this.deleteTransactionCategory = this.deleteTransactionCategory.bind(this);
    this.getStatements = this.getStatements.bind(this);
    this.createStatement = this.createStatement.bind(this);
    this.uploadStatement = this.uploadStatement.bind(this);
    this.downloadStatement = this.downloadStatement.bind(this);
    this.deleteStatement = this.deleteStatement.bind(this);
    this.getFiles = this.getFiles.bind(this);
    this.downloadFile = this.downloadFile.bind(this);
    this.getFileTypes = this.getFileTypes.bind(this);
    this.getTasks = this.getTasks.bind(this);
    this.getChannel = this.getChannel.bind(this);
    this.getMeetings = this.getMeetings.bind(this);
    this.getCountOfUnread = this.getCountOfUnread.bind(this);
    this.getUpcomingDates = this.getUpcomingDates.bind(this);
    this.cancelAll = this.cancelAll.bind(this);
    this._mergeControllers = this._mergeControllers.bind(this);
    this._controllers = {};
  }

  _mergeControllers(controllerObj = {}) {
    this._controllers = { ...this._controllers, ...controllerObj };
  }

  cancelAll() {
    Object.values(this._controllers)
      .filter((controller) => !controller?.signal?.aborted)
      .forEach((controller) => {
        controller?.abort();
      });
    this._controllers = {};
  }

  getPlaidToken({ customerId, productId }) {
    const path = `/customers/${customerId}/products/${productId}/plaid-link-token`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  addPlaidAccount({ customerId, productId, publicKey }) {
    const path = `/customers/${customerId}/products/${productId}/plaid-accounts/public-key/${publicKey}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, {}, { signal: controller.signal });
  }

  getOAuthUrl({ customerId, productId }) {
    const path = `/customers/${customerId}/products/${productId}/get-oauth-url`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  authenticate({ customerId, productId, code, state, redirectUrl }) {
    const path = `/customers/${customerId}/products/${productId}/accounting-authentication`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, {}, { params: { code, state, redirectUrl }, signal: controller.signal });
  }

  getAccounts({ customerId, productId }) {
    const path = `/customers/${customerId}/products/${productId}/accounts`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  addAccount({ customerId, productId, body = {} }) {
    const path = `/customers/${customerId}/products/${productId}/add-account`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, body, { signal: controller.signal });
  }

  deleteAccount({ customerId, productId, accountId }) {
    const path = `/customers/${customerId}/products/${productId}/accounts/${accountId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.delete(path, { signal: controller.signal });
  }

  getMessages({ customerId, page, count, type, from, to, newOnly = false }) {
    const path = `/customers/${customerId}/messages`;
    const params = { newOnly };

    if (from) {
      params.from = from;
    }

    if (to) {
      params.to = to;
    }

    if (type) {
      params.type = type;
    }

    if (page) {
      params.page = page;
    }

    if (count) {
      params.count = count;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, { params, signal: controller.signal });
  }

  postMessage({ body = {} }) {
    const path = `/customers/messages`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, body, { signal: controller.signal });
  }

  markMessages({ customerId, messages }) {
    const path = `/customers/${customerId}/messages/mark-read`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(
      path,
      {},
      { params: { messages }, paramsSerializer: { indexes: null }, signal: controller.signal }
    );
  }

  getGraph({ customerId, productId, from, to, mergeTo, type }) {
    const path = `/customers/${customerId}/products/${productId}/transactions-dashboard`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { params: { from, to, mergeTo, type }, signal: controller.signal });
  }

  getBankTransactions({ customerId, productId, from, to, page, count, isReconcile }) {
    const path = `/customers/${customerId}/products/${productId}/bank-transactions`;
    const params = { from, to, page, count };

    if (isReconcile !== null) {
      params.isReconcile = isReconcile;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, { params, signal: controller.signal });
  }

  addBankTransaction({ customerId, productId, body = {} }) {
    const path = `/customers/${customerId}/products/${productId}/transactions`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, body, { signal: controller.signal });
  }

  updateBankTransaction({ customerId, productId, transactionId, body = {} }) {
    const path = `/customers/${customerId}/products/${productId}/transactions/${transactionId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.put(path, body, { signal: controller.signal });
  }

  deleteBankTransaction({ customerId, productId, transactionId }) {
    const path = `/customers/${customerId}/products/${productId}/transactions/${transactionId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.delete(path, { signal: controller.signal });
  }

  getTransactionCategories({ customerId }) {
    const path = `/customers/${customerId}/transaction-category`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  addTransactionCategory({ customerId, category }) {
    const path = `/customers/${customerId}/transaction-category`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, { category }, { signal: controller.signal });
  }

  deleteTransactionCategory({ customerId, categoryId }) {
    const path = `/customers/${customerId}/transaction-category/${categoryId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.delete(path, { signal: controller.signal });
  }

  getStatements({
    customerId,
    productId,
    page,
    count,
    type,
    status,
    year,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
  }) {
    const path = `/customers/${customerId}/products/${productId}/statements`;
    const params = { page, count };

    if (type) {
      params.type = type;
    }

    if (status) {
      params.status = status;
    }

    if (year) {
      params.year = year;
    }

    if (startDateFrom) {
      params.startDateFrom = startDateFrom;
    }

    if (startDateTo) {
      params.startDateTo = startDateTo;
    }

    if (endDateFrom) {
      params.endDateFrom = endDateFrom;
    }

    if (endDateTo) {
      params.endDateTo = endDateTo;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, {
      params,
      paramsSerializer: {
        indexes: null,
      },
      signal: controller.signal,
    });
  }

  createStatement({ customerId, productId, type, year, startDate, endDate, description, body }) {
    const path = `/customers/${customerId}/products/${productId}/statements`;
    const params = {};

    if (type) {
      params.type = type;
    }

    if (year) {
      params.year = year;
    }

    if (startDate) {
      params.startDate = startDate;
    }

    if (endDate) {
      params.endDate = endDate;
    }

    if (description) {
      params.description = description;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.post(path, body, {
      params,
      paramsSerializer: {
        indexes: null,
      },
      signal: controller.signal,
    });
  }

  uploadStatement({ customerId, productId, statementId, body }) {
    const path = `/customers/${customerId}/products/${productId}/statements/${statementId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, body, { signal: controller.signal });
  }

  downloadStatement({ customerId, productId, statementId }) {
    const path = `/customers/${customerId}/products/${productId}/statements/${statementId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { responseType: 'blob', signal: controller.signal });
  }

  deleteStatement({ customerId, productId, statementId }) {
    const path = `/customers/${customerId}/products/${productId}/statements/${statementId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.delete(path, { signal: controller.signal });
  }

  getFiles({ customerId, productId, page, count, type, year, startDateFrom, startDateTo, endDateFrom, endDateTo }) {
    const path = `/customers/${customerId}/products/${productId}/files`;
    const params = { page, count };

    if (type) {
      params.type = type;
    }

    if (year) {
      params.year = year;
    }

    if (startDateFrom) {
      params.startDateFrom = startDateFrom;
    }

    if (startDateTo) {
      params.startDateTo = startDateTo;
    }

    if (endDateFrom) {
      params.endDateFrom = endDateFrom;
    }

    if (endDateTo) {
      params.endDateTo = endDateTo;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, { params, signal: controller.signal });
  }

  downloadFile({ customerId, productId, fileId }) {
    const path = `/customers/${customerId}/products/${productId}/files/${fileId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { responseType: 'blob', signal: controller.signal });
  }

  getFileTypes({ customerId, productId }) {
    const path = `/customers/${customerId}/products/${productId}/file-types`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  getTasks({ customerId, orderId, page, count, status }) {
    const path = `/customers/${customerId}/orders/${orderId}/tasks`;
    const params = { page, count };

    if (status) {
      params.status = status;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, {
      params,
      paramsSerializer: {
        indexes: null,
      },
      signal: controller.signal,
    });
  }

  getChannel({ customerId, productId }) {
    const path = `/customers/${customerId}/products/${productId}/get-message-channel`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  getMeetings({ customerId }) {
    const path = `/customers/${customerId}/meetings`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  getCountOfUnread({ customerId }) {
    const path = `/customers/${customerId}/message-channels/count-of-unread-messages`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  getUpcomingDates({ code }) {
    const path = `/accounting/upcoming-dates`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { params: { code }, signal: controller.signal });
  }
}

const ApiInstance = new AccountingApi(accountingApiUrl);

export { ApiInstance as AccountingApi };
